/**
 * /today — world-class daily operations dashboard.
 *
 * Top-of-page metric cards (Occupancy / Today's revenue / Pets in care) with
 * drill-down drawer on click. AI brief banner pulled from the cached
 * /api/copilot/anomalies + the daily_brief AI summary. Realtime facility
 * floor plan grouped by building with drag-and-drop pet reassignment.
 * Mini 06:00-22:00 horizontal timeline. Smart Suggestions right-side rail
 * (vaccine expiry + dormant customers + late-pickup stays + open POS).
 * Click-to-message owner from any pet card. The pre-existing grooming queue
 * panel + arrivals/departures lists are kept so the older Today tests still pass.
 */
import { useAuthContext } from '@/auth/useAuthContext';
import { BookingRequestsPanel } from '@/components/BookingRequestsPanel';
import { CountCard } from '@/components/CountCard';
import { ExpectedArrivalsRail } from '@/components/ExpectedArrivalsRail';
import { FeedingTicks } from '@/components/FeedingTicks';
import { Modal } from '@/components/Modal';
import { NoShowBadge } from '@/components/NoShowBadge';
import { SendReportCardButton } from '@/components/ReportCardsPanel';
import { SkeletonRow } from '@/components/SkeletonRow';
import { StatusPill } from '@/components/StatusPill';
import { DailyCheckModal } from '@/components/compliance/DailyCheckModal';
import { RecordCleaningModal } from '@/components/compliance/RecordCleaningModal';
import {
  type GroomingAppointmentRow,
  type PendingGroomingQueueEntry,
  type TodaySnapshot,
  type TodaySnapshotEvent,
  type TodaySnapshotOccupant,
  type TodaySnapshotRun,
  type TodaySnapshotSuggestion,
  fetchCopilotAnomalies,
  fetchTodaySnapshot,
  listGroomers,
  listGroomingAppointments,
  listPendingGroomingQueue,
  listServiceTasks,
  moveBoardingStayRun,
  openBatchRunCardsForPrint,
  searchBookings,
} from '@/lib/api';
import { formatPriceMinor } from '@/lib/format';
import type { BookingSearchRow, Groomer, ServiceTaskRow } from '@/lib/types';
import { useRealtime } from '@/lib/useRealtime';
import { useSpaceNoun } from '@/lib/useSpaceNoun';
import {
  AlertTriangle,
  ArrowDown,
  ArrowDownRight,
  ArrowUp,
  ArrowUpRight,
  CheckCircle2,
  Circle,
  ClipboardCheck,
  Clock,
  Droplets,
  Home,
  LayoutGrid,
  List,
  MessageSquare,
  Moon,
  Printer,
  Scissors,
  Sparkles,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/** Derive the YYYY-MM-DD string for "today" in the given timezone. */
function todayInTz(timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: timezone, dateStyle: 'short' }).format(
    new Date(),
  );
}

/**
 * Migration 0103 — whole calendar days a checked-in stay is past its
 * scheduled checkout day in the facility's zone (0 when inside the window).
 */
export function overstayNightsFor(
  occupant: Pick<TodaySnapshotOccupant, 'status' | 'checkout'>,
  timezone: string,
  localToday: string = todayInTz(timezone),
): number {
  if (occupant.status !== 'checked_in') return 0;
  const scheduled = occupant.checkout.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(scheduled) || localToday <= scheduled) return 0;
  const toMs = (d: string) => {
    const [y, m, day] = d.split('-').map((n) => Number.parseInt(n, 10)) as [number, number, number];
    return Date.UTC(y, m - 1, day);
  };
  return Math.max(0, Math.round((toMs(localToday) - toMs(scheduled)) / 86_400_000));
}

/** Format a human-readable date string in the given timezone. */
function formatTodayLabel(timezone: string, locale = 'en-GB'): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'full',
    timeZone: timezone,
  }).format(new Date());
}

function formatTimeShort(iso: string, tz: string, locale = 'en-GB'): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: tz,
    }).format(new Date(iso));
  } catch {
    return iso.slice(11, 16);
  }
}

/** Compact live clock — re-renders every minute. */
function useLiveClock(tz: string, locale = 'en-GB'): string {
  const fmt = () =>
    new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: tz,
    }).format(new Date());
  const [time, setTime] = useState(fmt);
  useEffect(() => {
    const id = setInterval(() => setTime(fmt()), 30_000);
    return () => clearInterval(id);
  }, [tz]);
  return time;
}

interface TodayData {
  checkingIn: BookingSearchRow[];
  checkingOut: BookingSearchRow[];
  /** close-enough proxy: bookings with status=checked_in */
  boarded: BookingSearchRow[];
  tasks: ServiceTaskRow[];
  /** Sub-spec 05 Plan 4 — grooming sub-bookings for today's checkouts that
   *  still need a groomer assigned. */
  pendingGrooming: PendingGroomingQueueEntry[];
  /** Today's confirmed/checked-in standalone grooming appointments. */
  groomingAppointments: GroomingAppointmentRow[];
  /** Map of groomerId → displayName for enriching groomingAppointments. */
  groomersById: Map<string, string>;
}

export function Today() {
  const { state } = useAuthContext();
  const noun = useSpaceNoun();
  const { me } = state;

  const facilityId = me?.activeFacility?.id ?? null;
  const timezone = me?.activeFacility?.timezone ?? me?.activeAccount?.defaultTimezone ?? 'UTC';

  // Must be called before any early return to comply with the Rules of Hooks.
  const clockStr = useLiveClock(timezone);

  const [data, setData] = useState<TodayData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [snapshot, setSnapshot] = useState<TodaySnapshot | null>(null);
  const [anomalies, setAnomalies] = useState<
    Array<{ kind: string; severity: 'info' | 'warn' | 'critical'; message: string }>
  >([]);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);

  const [drawerOccupant, setDrawerOccupant] = useState<TodaySnapshotOccupant | null>(null);
  const [drawerOccupants, setDrawerOccupants] = useState<TodaySnapshotOccupant[] | null>(null);
  const [drawerMetric, setDrawerMetric] = useState<'occupancy' | 'revenue' | 'pets' | null>(null);
  const [briefExpanded, setBriefExpanded] = useState(false);
  // Migration 0100 — licensing quick actions (record a clean / daily check).
  const [cleaningOpen, setCleaningOpen] = useState(false);
  const [dailyCheckOccupant, setDailyCheckOccupant] = useState<TodaySnapshotOccupant | null>(null);
  // Migration 0095 — bumped on realtime booking events so the Requests
  // section re-fetches without opening its own WebSocket.
  const [requestsTick, setRequestsTick] = useState(0);

  const facilityIdRef = useRef(facilityId);
  facilityIdRef.current = facilityId;

  // Today's ISO-date string in the facility's tz — recomputed when tz changes.
  // Used both by data fetches AND by the Print Run Cards header button.
  const today = useMemo(() => todayInTz(timezone), [timezone]);

  const fetchAll = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);
    try {
      // Fetch checking-in rows: all pre-checkin statuses arriving today.
      // Using checkInDate (exact) instead of from+to which was logically impossible
      // for multi-night stays (check_in >= today AND checkout <= today).
      const [
        inRowsConfirmed,
        inRowsDeposit,
        inRowsPaid,
        outRows,
        boardedRows,
        tasks,
        pendingGroomingRes,
        groomingApptRows,
        groomersRes,
      ] = await Promise.all([
        searchBookings({ checkInDate: today, facilityId, status: 'confirmed' }),
        searchBookings({ checkInDate: today, facilityId, status: 'confirmed_deposit' }),
        searchBookings({ checkInDate: today, facilityId, status: 'paid' }),
        searchBookings({ checkoutDate: today, facilityId, status: 'checked_in' }),
        searchBookings({ facilityId, status: 'checked_in' }),
        listServiceTasks(today, facilityId),
        listPendingGroomingQueue({ facilityId, date: today }),
        listGroomingAppointments({ date: today, facilityId }),
        listGroomers(),
      ]);
      const inRows = [...inRowsConfirmed, ...inRowsDeposit, ...inRowsPaid];
      const boarded = boardedRows.filter((r) => r.checkout > today);
      // Keep only standalone confirmed/checked-in grooming appointments
      // (exclude sub-bookings that are already shown in the pending-grooming queue).
      const standaloneGrooming = groomingApptRows.filter(
        (r) => r.booking.status === 'confirmed' || r.booking.status === 'checked_in',
      );
      const groomersById = new Map<string, string>(
        groomersRes.groomers.map((g: Groomer) => [g.id, g.displayName]),
      );
      setData({
        checkingIn: inRows,
        checkingOut: outRows,
        boarded,
        tasks,
        pendingGrooming: pendingGroomingRes.entries,
        groomingAppointments: standaloneGrooming,
        groomersById,
      });
    } catch (e: unknown) {
      setError((e as Error).message ?? 'Failed to load today overview');
    } finally {
      setLoading(false);
    }
  }, [facilityId, today]);

  const fetchSnapshot = useCallback(async () => {
    if (!facilityId) return;
    setSnapshotError(null);
    try {
      const snap = await fetchTodaySnapshot(facilityId);
      // API returns { empty: true } when the facility isn't found (e.g. stale
      // facilityId after a facility is deleted). Guard against rendering a
      // malformed snapshot object.
      if (!snap || 'empty' in snap) return;
      setSnapshot(snap);
    } catch (e: unknown) {
      setSnapshotError((e as Error).message ?? 'Failed to load snapshot');
    }
    try {
      const result = await fetchCopilotAnomalies();
      setAnomalies(result.anomalies as typeof anomalies);
    } catch {
      setAnomalies([]);
    }
  }, [facilityId]);

  useEffect(() => {
    void fetchAll();
    void fetchSnapshot();
  }, [fetchAll, fetchSnapshot]);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const snapshotDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useRealtime(
    useCallback(
      (event) => {
        const isRelevant =
          event.type === 'BOOKING_CREATED' ||
          event.type === 'BOOKING_UPDATED' ||
          event.type === 'SERVICE_UPDATED' ||
          event.type === 'BOARDING_STAY_MOVED' ||
          event.type === 'BOOKING_PHOTO_POSTED' ||
          event.type === 'BOOKING_MESSAGE_POSTED';
        if (!isRelevant) return;
        const evFacility = 'facility_id' in event ? event.facility_id : null;
        if (facilityIdRef.current && evFacility && evFacility !== facilityIdRef.current) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          void fetchAll();
          setRequestsTick((t) => t + 1);
        }, 500);
        if (snapshotDebounceRef.current) clearTimeout(snapshotDebounceRef.current);
        snapshotDebounceRef.current = setTimeout(() => {
          void fetchSnapshot();
        }, 250);
      },
      [fetchAll, fetchSnapshot],
    ),
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (snapshotDebounceRef.current) clearTimeout(snapshotDebounceRef.current);
    };
  }, []);

  if (!facilityId) {
    const hasFacilities = (me?.facilitiesInScope?.length ?? 0) > 0;
    return (
      <div className="mx-auto max-w-5xl px-6 py-10 md:px-10">
        <h1 className="mb-2 font-heading text-2xl font-medium tracking-tight">Today</h1>
        <div className="rounded-xl border border-border bg-surface px-6 py-12 text-center">
          {hasFacilities ? (
            <>
              <p className="text-sm text-muted-foreground">
                Select a facility to see today&rsquo;s overview.
              </p>
              <p className="mt-1 text-xs text-muted-foreground/70">
                Use the facility picker in the rail on the left.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-medium">No facilities set up yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Set up your first facility and {noun.plural} to start tracking today&rsquo;s
                bookings.
              </p>
              <Link
                to="/settings/runs"
                className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
              >
                Set up {noun.plural} in Settings
              </Link>
            </>
          )}
        </div>
      </div>
    );
  }

  const todayLabel = formatTodayLabel(timezone);
  const facilityName = me?.activeFacility?.name ?? '';

  const doneCount = data?.tasks.filter((t) => t.status === 'done').length ?? 0;
  const totalTasks = data?.tasks.length ?? 0;
  const taskPct = totalTasks > 0 ? Math.round((doneCount / totalTasks) * 100) : 0;

  const occMetric = snapshot?.metrics.occupancy;
  const revMetric = snapshot?.metrics.revenue;
  const petsMetric = snapshot?.metrics.petsInCare;

  // Derive occupancy accent color based on fill percentage.
  const occPct = occMetric?.occupancyPct ?? 0;
  const occAccent = occPct >= 90 ? 'destructive' : occPct >= 70 ? 'warning' : 'success';

  // Status bar: all clear or summarise anomalies.
  const criticalAnomalies = anomalies.filter((a) => a.severity === 'critical');
  const warnAnomalies = anomalies.filter((a) => a.severity === 'warn');
  const systemStatus =
    criticalAnomalies.length > 0 ? 'critical' : warnAnomalies.length > 0 ? 'warn' : 'ok';

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 md:px-10">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
            {facilityName ?? 'Dashboard'}
          </p>
          <h1 className="font-heading text-2xl font-medium tracking-tight">Today</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            <span className="num">{todayLabel}</span>
          </p>
        </div>
        {facilityId && <PrintTodaysRunCards facilityId={facilityId} date={today} />}
      </div>

      {/* Migration 0095 — booking requests awaiting approval (renders nothing when empty) */}
      <BookingRequestsPanel facilityId={facilityId} refreshKey={requestsTick} />

      {/* AI Brief banner */}
      {snapshot?.aiBrief && (
        <div
          className="mb-6 rounded-xl border border-border bg-gradient-to-br from-primary/5 to-transparent px-5 py-4"
          data-testid="today-ai-brief"
        >
          <div className="flex items-start gap-3">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                AI brief — {snapshot.today}
              </p>
              <p className={`mt-1 text-sm leading-relaxed ${briefExpanded ? '' : 'line-clamp-2'}`}>
                {snapshot.aiBrief}
              </p>
              <button
                type="button"
                className="mt-2 text-xs font-medium text-primary hover:underline"
                onClick={() => setBriefExpanded((v) => !v)}
              >
                {briefExpanded ? 'Collapse' : 'Read full brief'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-6 rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}
      {snapshotError && (
        <div className="mb-6 rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {snapshotError}
        </div>
      )}

      {/* Status bar */}
      <div
        className="mb-5 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-lg border border-border bg-surface px-4 py-2"
        data-testid="today-status-bar"
      >
        <div className="flex min-h-11 items-center gap-2 md:min-h-0">
          {systemStatus === 'ok' ? (
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-success" aria-hidden />
          ) : systemStatus === 'warn' ? (
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-warning-foreground" aria-hidden />
          ) : (
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-destructive" aria-hidden />
          )}
          <span
            className={`text-xs font-medium ${
              systemStatus === 'ok'
                ? 'text-success'
                : systemStatus === 'warn'
                  ? 'text-warning-foreground'
                  : 'text-destructive'
            }`}
          >
            {systemStatus === 'ok'
              ? 'All systems normal'
              : systemStatus === 'warn'
                ? `${warnAnomalies.length} warning${warnAnomalies.length !== 1 ? 's' : ''} flagged`
                : `${criticalAnomalies.length} critical alert${criticalAnomalies.length !== 1 ? 's' : ''}`}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          {facilityId && (
            <button
              type="button"
              onClick={() => setCleaningOpen(true)}
              className="press flex min-h-11 items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs font-medium text-foreground hover:bg-muted md:min-h-0"
              title="Record a cleaning / disinfection event"
            >
              <Droplets className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
              Record cleaning
            </button>
          )}
          {facilityName && (
            <span className="hidden truncate max-w-[180px] sm:block">{facilityName}</span>
          )}
          <span className="num flex min-h-11 items-center gap-1 md:min-h-0">
            <Clock className="h-3 w-3" aria-hidden />
            {clockStr}
          </span>
        </div>
      </div>

      {/* Top metric cards */}
      <div className="mb-6 grid gap-4 sm:grid-cols-3" data-testid="today-metrics">
        <MetricCard
          label="Occupancy"
          value={occMetric ? `${occMetric.occupiedCount}/${occMetric.totalCapacity}` : '—'}
          subValue={
            occMetric
              ? `${occMetric.occupancyPct}% full · ${occMetric.totalCapacity - occMetric.occupiedCount} ${noun.plural} available`
              : ''
          }
          delta={occMetric?.delta ?? null}
          onClick={() => setDrawerMetric('occupancy')}
          testId="metric-occupancy"
          fillPct={occMetric?.occupancyPct}
          accent={occAccent}
        />
        <MetricCard
          label="Today's revenue"
          value={revMetric ? formatPriceMinor(revMetric.totalMinor, revMetric.currency) : '—'}
          subValue={revMetric ? `${revMetric.paymentCount} payments` : ''}
          delta={null}
          onClick={() => setDrawerMetric('revenue')}
          testId="metric-revenue"
          accent="primary"
        />
        <MetricCard
          label="Pets in care"
          value={petsMetric ? String(petsMetric.total) : '—'}
          subValue={
            petsMetric
              ? `${petsMetric.boarding} boarding · ${petsMetric.daycare} daycare · ${petsMetric.grooming} grooming`
              : ''
          }
          delta={null}
          onClick={() => setDrawerMetric('pets')}
          testId="metric-pets"
          accent="primary"
        />
      </div>

      {/* Main grid: floor plan + timeline (left) + suggestions (right) */}
      <div className="mb-6 grid gap-6 lg:grid-cols-[1fr_320px]" data-testid="today-main-grid">
        {/* Left column — min-w-0 so wide run strips scroll inside instead of
            widening the column (grid children default to min-width:auto). */}
        <div className="min-w-0 space-y-6">
          {snapshot && (
            <FloorPlan
              snapshot={snapshot}
              onOccupantClick={setDrawerOccupant}
              onMultiOccupantsClick={setDrawerOccupants}
              onMoved={fetchSnapshot}
            />
          )}
          {snapshot && (
            <Timeline events={snapshot.events} tz={snapshot.timezone} onPick={setDrawerOccupant} />
          )}
        </div>

        {/* Right column: expected-arrivals + suggestions */}
        <div className="min-w-0 space-y-4">
          <ExpectedArrivalsRail facilityId={facilityId} />
          <SuggestionsRail suggestions={snapshot?.suggestions ?? []} anomalies={anomalies} />
        </div>
      </div>

      {/* Task progress */}
      <div className="mb-6 rounded-xl border border-border bg-surface px-5 py-4">
        <p className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          Task progress
        </p>
        {loading ? (
          <div className="h-4 w-40 animate-pulse rounded bg-secondary" />
        ) : (
          <>
            <p className="num mb-2 text-sm font-medium tabular-nums">
              <span>{doneCount}</span>
              <span className="text-muted-foreground"> of </span>
              <span>{totalTasks}</span>
              <span className="text-muted-foreground"> tasks done</span>
            </p>
            <div
              className="h-1.5 w-full overflow-hidden rounded-full bg-secondary"
              role="progressbar"
              tabIndex={-1}
              aria-valuenow={taskPct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${taskPct}% of tasks done`}
            >
              <div
                className="h-full rounded-full bg-emerald-500 transition-all"
                style={{ width: `${taskPct}%` }}
              />
            </div>
          </>
        )}
      </div>

      {/* Count cards — kept for legacy + small-screen quick-look */}
      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <CountCard
          icon={ArrowDown}
          label="Checking in"
          count={data?.checkingIn.length ?? 0}
          loading={loading}
          names={data?.checkingIn.map((r) => r.petName) ?? []}
        />
        <CountCard
          icon={ArrowUp}
          label="Checking out"
          count={data?.checkingOut.length ?? 0}
          loading={loading}
          names={data?.checkingOut.map((r) => r.petName) ?? []}
        />
        <CountCard
          icon={Home}
          label="Boarded"
          count={data?.boarded.length ?? 0}
          loading={loading}
          names={data?.boarded.map((r) => r.petName) ?? []}
        />
      </div>

      {/* Grooming queue — sub-spec 05 Plan 4 M3 */}
      {data && data.pendingGrooming.length > 0 && (
        <section className="mb-6" data-testid="grooming-queue">
          <h2 className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            Grooming queue — needs groomer assignment
          </h2>
          <div className="rounded-xl border border-border bg-surface">
            <ul className="divide-y divide-border">
              {data.pendingGrooming.map((entry) => (
                <GroomingQueueItem key={entry.booking.id} entry={entry} />
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Today's grooming schedule */}
      {data && (
        <section className="mb-6" data-testid="grooming-schedule">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              Today&rsquo;s grooming appointments
            </h2>
            <Link
              to="/bookings/new?step=3&serviceType=grooming"
              className="text-xs font-medium text-primary hover:underline flex items-center gap-1"
            >
              <Scissors className="size-3" strokeWidth={1.75} />
              Walk-in
            </Link>
          </div>
          {data.groomingAppointments.length > 0 ? (
            <div className="rounded-xl border border-border bg-surface">
              <ul className="divide-y divide-border">
                {data.groomingAppointments.map((row) => (
                  <GroomingScheduleItem
                    key={row.booking.id}
                    row={row}
                    groomerName={
                      row.appointment.groomerId
                        ? (data.groomersById.get(row.appointment.groomerId) ?? null)
                        : null
                    }
                    snapshot={snapshot}
                    timezone={timezone}
                  />
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No grooming appointments today.</p>
          )}
        </section>
      )}

      {/* Arriving + Departing panels */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Arriving */}
        <section>
          <h2 className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            Arriving today
          </h2>
          <div className="rounded-xl border border-border bg-surface">
            {loading ? (
              <>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </>
            ) : (data?.checkingIn.length ?? 0) === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                No arrivals today.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {data?.checkingIn.map((row) => (
                  <BookingListItem key={row.bookingId} row={row} />
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* Departing */}
        <section>
          <h2 className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            Departing today
          </h2>
          <div className="rounded-xl border border-border bg-surface">
            {loading ? (
              <>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </>
            ) : (data?.checkingOut.length ?? 0) === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                No departures today.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {data?.checkingOut.map((row) => (
                  <BookingListItem key={row.bookingId} row={row} />
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      {/* Metric drill-down drawer */}
      {drawerMetric && snapshot && (
        <Modal onClose={() => setDrawerMetric(null)} aria-label="Metric detail">
          <MetricDrawer
            metric={drawerMetric}
            snapshot={snapshot}
            onClose={() => setDrawerMetric(null)}
          />
        </Modal>
      )}

      {/* Occupant detail drawer — single pet */}
      {drawerOccupant && (
        <Modal onClose={() => setDrawerOccupant(null)} aria-label="Pet detail">
          <OccupantDrawer
            occupant={drawerOccupant}
            tz={timezone}
            onClose={() => setDrawerOccupant(null)}
            onDailyCheck={(o) => {
              setDrawerOccupant(null);
              setDailyCheckOccupant(o);
            }}
          />
        </Modal>
      )}

      {/* Migration 0100 — daily monitoring tick for one occupant */}
      {dailyCheckOccupant?.bookingId && (
        <DailyCheckModal
          bookingId={dailyCheckOccupant.bookingId}
          petName={dailyCheckOccupant.petName}
          onClose={() => setDailyCheckOccupant(null)}
        />
      )}

      {/* Migration 0100 — cleaning + disinfection record */}
      {cleaningOpen && facilityId && (
        <RecordCleaningModal
          facilityId={facilityId}
          runs={(snapshot?.floorPlan.runs ?? []).map((r) => ({ id: r.id, name: r.name }))}
          onClose={() => setCleaningOpen(false)}
        />
      )}

      {/* Multi-occupant drawer — run with 2+ pets */}
      {drawerOccupants && (
        <Modal onClose={() => setDrawerOccupants(null)} aria-label={`${noun.Singular} occupants`}>
          <MultiOccupantDrawer
            occupants={drawerOccupants}
            tz={timezone}
            onClose={() => setDrawerOccupants(null)}
            onSelect={(o) => {
              setDrawerOccupants(null);
              setDrawerOccupant(o);
            }}
          />
        </Modal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface MetricCardProps {
  label: string;
  value: string;
  subValue: string;
  delta: number | null;
  onClick: () => void;
  testId: string;
  /** 0–100 fill bar value (occupancy pct). Omit or pass undefined for no bar. */
  fillPct?: number | undefined;
  /** Color accent for the fill bar and value text. */
  accent?: 'success' | 'warning' | 'destructive' | 'primary' | undefined;
}
function MetricCard({
  label,
  value,
  subValue,
  delta,
  onClick,
  testId,
  fillPct,
  accent = 'primary',
}: MetricCardProps) {
  const deltaPositive = delta !== null && delta > 0;
  const deltaNegative = delta !== null && delta < 0;

  const barColor =
    accent === 'success'
      ? 'bg-success'
      : accent === 'warning'
        ? 'bg-warning'
        : accent === 'destructive'
          ? 'bg-destructive'
          : 'bg-primary';

  const valueColor =
    accent === 'success'
      ? 'text-success'
      : accent === 'warning'
        ? 'text-warning-foreground'
        : accent === 'destructive'
          ? 'text-destructive'
          : 'text-foreground';

  return (
    <button
      type="button"
      onClick={onClick}
      className="group min-h-11 w-full rounded-xl border border-border bg-surface px-5 py-4 text-left transition hover:border-primary/40 hover:shadow-sm"
      data-testid={testId}
    >
      <p className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
        {label}
      </p>
      <p
        className={`num mt-1.5 font-mono text-3xl font-semibold tracking-tight tabular-nums ${valueColor}`}
      >
        {value}
      </p>
      {fillPct !== undefined && (
        <div
          className="relative mt-2 h-1 w-full overflow-hidden rounded-full bg-border"
          role="presentation"
        >
          <div
            className={`h-full rounded-full transition-all duration-500 ${barColor}`}
            style={{ width: `${Math.min(100, fillPct)}%` }}
          />
        </div>
      )}
      <div className="mt-1.5 flex items-center gap-2">
        {subValue && <p className="truncate text-xs text-muted-foreground">{subValue}</p>}
        {delta !== null && delta !== 0 && (
          <span
            className={`ml-auto inline-flex shrink-0 items-center gap-0.5 text-xs font-medium ${
              deltaPositive ? 'text-success' : deltaNegative ? 'text-warning-foreground' : ''
            }`}
          >
            {deltaPositive ? (
              <ArrowUpRight className="h-3 w-3" aria-hidden />
            ) : (
              <ArrowDownRight className="h-3 w-3" aria-hidden />
            )}
            <span className="num">{Math.abs(delta)}</span>
            <span className="text-muted-foreground">vs last wk</span>
          </span>
        )}
      </div>
    </button>
  );
}

interface FloorPlanProps {
  snapshot: TodaySnapshot;
  /** Single-occupant click — opens the occupant detail drawer. */
  onOccupantClick: (o: TodaySnapshotOccupant) => void;
  /** Multi-occupant click — opens a list drawer for all occupants in the run. */
  onMultiOccupantsClick: (os: TodaySnapshotOccupant[]) => void;
  onMoved: () => void;
}
function FloorPlan({ snapshot, onOccupantClick, onMultiOccupantsClick, onMoved }: FloorPlanProps) {
  const noun = useSpaceNoun();
  const totalRuns = snapshot.floorPlan.runs.length;
  // Default to compact list view for large facilities (> 30 runs).
  const [compactView, setCompactView] = useState(totalRuns > 30);
  const [searchQuery, setSearchQuery] = useState('');

  // Group runs by building. NULL building goes into "Ungrouped".
  const runsByBuilding = useMemo(() => {
    const m = new Map<string | null, TodaySnapshotRun[]>();
    for (const r of snapshot.floorPlan.runs) {
      const key = r.buildingId;
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(r);
    }
    return m;
  }, [snapshot.floorPlan.runs]);

  const buildings = snapshot.floorPlan.buildings;
  const ungroupedRuns = runsByBuilding.get(null) ?? [];

  // Collapsed-buildings state (keyed by building id or 'ungrouped').
  const [collapsedBuildings, setCollapsedBuildings] = useState<Set<string>>(new Set());
  const toggleBuilding = useCallback((key: string) => {
    setCollapsedBuildings((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const [dragBookingId, setDragBookingId] = useState<string | null>(null);
  const [dropTargetRunId, setDropTargetRunId] = useState<string | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);

  // Species of the pet being dragged — runs that don't take it are not
  // offered as drop targets (the API would 422 run_species_mismatch anyway).
  const dragSpecies = useMemo(() => {
    if (!dragBookingId) return null;
    for (const r of snapshot.floorPlan.runs) {
      const hit = r.occupants.find((o) => o.bookingId === dragBookingId);
      if (hit) return hit.petSpecies;
    }
    return (
      snapshot.floorPlan.unassignedStays.find((o) => o.bookingId === dragBookingId)?.petSpecies ??
      null
    );
  }, [dragBookingId, snapshot.floorPlan]);

  const handleDrop = useCallback(
    async (runId: string, bookingId: string) => {
      setDropTargetRunId(null);
      setDragBookingId(null);
      try {
        await moveBoardingStayRun(bookingId, runId);
        setMoveError(null);
        onMoved();
      } catch (e) {
        const err = e as { code?: string; message?: string };
        if (err.code === 'capacity_exceeded') {
          setMoveError('Destination run is at capacity.');
        } else if (err.code === 'run_species_mismatch') {
          const body = (err as { body?: { species?: string; speciesAllowed?: string[] } }).body;
          const allowed = body?.speciesAllowed ?? [];
          setMoveError(
            `That run does not take ${body?.species ?? 'this species'}s${
              allowed.length > 0 ? ` (${allowed.join(' / ')} only)` : ''
            }.`,
          );
        } else {
          setMoveError(err.message ?? 'Failed to move pet');
        }
      }
    },
    [onMoved],
  );

  const handleUnassign = useCallback(
    async (bookingId: string) => {
      try {
        await moveBoardingStayRun(bookingId, null);
        onMoved();
      } catch (e) {
        const err = e as Error;
        setMoveError(err.message ?? 'Failed to unassign');
      }
    },
    [onMoved],
  );

  // Filter runs by search query (run name or any occupant pet name).
  const filterRun = useCallback(
    (run: TodaySnapshotRun): boolean => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      if (run.name.toLowerCase().includes(q)) return true;
      return run.occupants.some((o) => o.petName.toLowerCase().includes(q));
    },
    [searchQuery],
  );

  const groups = useMemo(
    () => [
      ...buildings.map((b) => ({
        id: b.id as string | null,
        key: b.id,
        name: b.name,
        runs: (runsByBuilding.get(b.id) ?? []).filter(filterRun),
      })),
      ...(ungroupedRuns.length > 0
        ? [
            {
              id: null as string | null,
              key: 'ungrouped',
              name: 'Ungrouped',
              runs: ungroupedRuns.filter(filterRun),
            },
          ]
        : []),
    ],
    [buildings, runsByBuilding, ungroupedRuns, filterRun],
  );

  // Occupants click handler: single → onOccupantClick; multi → onMultiOccupantsClick.
  const handleOccupantsClick = useCallback(
    (occupants: TodaySnapshotOccupant[]) => {
      if (occupants.length === 0) return;
      const first = occupants[0];
      if (occupants.length === 1 && first) {
        onOccupantClick(first);
      } else {
        onMultiOccupantsClick(occupants);
      }
    },
    [onOccupantClick, onMultiOccupantsClick],
  );

  return (
    <section data-testid="floor-plan" className="min-w-0">
      {/* Mobile: title row, then the toolbar wraps onto its own line with a
          full-width search. Desktop: single row, search capped at 180px. */}
      <div className="mb-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <h2 className="flex shrink-0 items-center gap-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          <LayoutGrid className="h-3.5 w-3.5" aria-hidden /> Floor plan
        </h2>
        <div
          className="flex w-full min-w-0 flex-wrap items-center gap-2 md:w-auto md:flex-1 md:flex-nowrap md:justify-end"
          data-testid="floor-plan-toolbar"
        >
          {/* Search input */}
          <input
            type="search"
            placeholder={`Search ${noun.plural} or pets…`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-11 w-full min-w-0 rounded-md border border-border bg-background px-2.5 text-sm placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary/50 md:h-7 md:w-auto md:max-w-[180px] md:text-xs"
            aria-label={`Search ${noun.plural}`}
          />
          {/* View toggle (only shown when > 30 runs) */}
          {totalRuns > 30 && (
            <button
              type="button"
              onClick={() => setCompactView((v) => !v)}
              className="flex min-h-11 shrink-0 items-center gap-1 rounded-md border border-border bg-background px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-secondary md:min-h-0"
            >
              {compactView ? (
                <LayoutGrid className="h-3.5 w-3.5" aria-hidden />
              ) : (
                <List className="h-3.5 w-3.5" aria-hidden />
              )}
              {compactView ? 'Grid' : 'Compact'}
            </button>
          )}
          {snapshot.floorPlan.unassignedStays.length > 0 && (
            <span className="shrink-0 text-xs text-muted-foreground">
              <span className="num">{snapshot.floorPlan.unassignedStays.length}</span> unassigned
            </span>
          )}
        </div>
      </div>
      {moveError && (
        <div className="mb-3 rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {moveError}
        </div>
      )}
      <div className="min-w-0 space-y-4">
        {groups.map((group) => {
          if (group.runs.length === 0 && searchQuery) return null;
          const isCollapsed = collapsedBuildings.has(group.key);
          return (
            <div key={group.key} className="rounded-xl border border-border bg-surface px-4 py-3">
              {/* Building header — clickable to collapse */}
              <button
                type="button"
                onClick={() => toggleBuilding(group.key)}
                className="mb-2 flex min-h-11 w-full items-center justify-between text-left md:min-h-0"
              >
                <p className="text-xs font-medium text-muted-foreground">{group.name}</p>
                <span className="text-[10px] text-muted-foreground/60">
                  {isCollapsed ? '▸' : '▾'}
                  <span className="ml-1 num">
                    {group.runs.length} {group.runs.length === 1 ? noun.singular : noun.plural}
                  </span>
                </span>
              </button>
              {!isCollapsed &&
                (compactView ? (
                  /* Compact list view */
                  <ul className="divide-y divide-border">
                    {group.runs.map((run) => (
                      <RunCompactRow
                        key={run.id}
                        run={run}
                        onOccupantsClick={handleOccupantsClick}
                      />
                    ))}
                  </ul>
                ) : (
                  /* Grid view — phones get a snapping horizontal strip with the
                     last cell peeking past the right edge; lg+ is an auto-fill
                     grid of >=140px cells (a fixed 6-col grid overlapped cells
                     whenever the column dropped under the 140px floor). */
                  <div
                    className="flex snap-x snap-mandatory gap-2 overflow-x-auto pb-2 pr-8 lg:grid lg:grid-cols-[repeat(auto-fill,minmax(140px,1fr))] lg:snap-none lg:overflow-visible lg:pr-0"
                    data-testid={`run-strip-${group.key}`}
                  >
                    {group.runs.map((run) => (
                      <RunCell
                        key={run.id}
                        run={run}
                        isDropTarget={dropTargetRunId === run.id}
                        dragSpecies={dragSpecies}
                        onDragStart={setDragBookingId}
                        onDragEnter={() => setDropTargetRunId(run.id)}
                        onDragLeave={() => setDropTargetRunId(null)}
                        onDrop={handleDrop}
                        onOccupantsClick={handleOccupantsClick}
                      />
                    ))}
                  </div>
                ))}
            </div>
          );
        })}
        {snapshot.floorPlan.unassignedStays.length > 0 && (
          <div className="rounded-xl border border-dashed border-border bg-secondary/40 px-4 py-3">
            <p className="mb-2 text-xs font-medium text-muted-foreground">Unassigned</p>
            <div className="flex flex-wrap gap-2">
              {snapshot.floorPlan.unassignedStays.map((occ) => (
                <PetMiniCard
                  key={occ.bookingId}
                  occupant={occ}
                  draggable
                  onDragStart={setDragBookingId}
                  onClick={() => onOccupantClick(occ)}
                  onUnassign={() => handleUnassign(occ.bookingId)}
                  hideUnassign
                />
              ))}
            </div>
          </div>
        )}
      </div>
      {dragBookingId && (
        <p className="mt-2 text-xs text-muted-foreground">
          Drop on a {noun.singular} to reassign
          {dragSpecies ? ` — only ${noun.plural} that take ${dragSpecies}s are offered` : ''}. Esc
          to cancel.
        </p>
      )}
    </section>
  );
}

interface RunCellProps {
  run: TodaySnapshotRun;
  isDropTarget: boolean;
  /** Species of the pet currently being dragged, or null when nothing is. */
  dragSpecies: string | null;
  onDragStart: (bookingId: string) => void;
  onDragEnter: () => void;
  onDragLeave: () => void;
  onDrop: (runId: string, bookingId: string) => void;
  /** Called with all occupants in this run when the user clicks a pet card. */
  onOccupantsClick: (occupants: TodaySnapshotOccupant[]) => void;
}
function RunCell({
  run,
  isDropTarget,
  dragSpecies,
  onDragStart,
  onDragEnter,
  onDragLeave,
  onDrop,
  onOccupantsClick,
}: RunCellProps) {
  // A run only accepts the species it was set up for. While a pet of another
  // species is being dragged the cell is dimmed and refuses the drop.
  const rejectsDrag = dragSpecies !== null && !run.speciesAllowed.includes(dragSpecies);
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (rejectsDrag) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    e.dataTransfer.dropEffect = 'move';
    onDragEnter();
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (rejectsDrag) return;
    const bookingId = e.dataTransfer.getData('text/booking-id');
    if (bookingId) onDrop(run.id, bookingId);
  };

  const occupied = run.occupants.length > 0;
  // Primary occupant is used for the drag handle. When there are multiple pets
  // in a run, any drag starts from occupants[0] — the operator can re-drag
  // individual pets from the booking detail page.
  const primaryOccupant = run.occupants[0] ?? null;
  const extraCount = run.occupants.length - 1;

  // Color-code run cells by occupancy state.
  const cellBg = isDropTarget
    ? 'border-primary bg-primary/5'
    : occupied
      ? 'border-success/30 bg-success/5'
      : 'border-dashed border-border';

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={onDragLeave}
      onDrop={handleDrop}
      className={`flex min-h-11 min-w-[140px] shrink-0 snap-start flex-col rounded-lg border px-3 py-2 transition lg:shrink ${cellBg} ${
        rejectsDrag ? 'opacity-40' : ''
      }`}
      data-testid={`run-cell-${run.id}`}
      data-rejects-drag={rejectsDrag ? 'true' : undefined}
      title={
        rejectsDrag
          ? `Does not take ${dragSpecies}s (${run.speciesAllowed.join(' / ') || 'no species'} only)`
          : undefined
      }
    >
      <p className="num truncate text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {run.name}
        {run.capacityMax > 1 && (
          <span className="ml-1 opacity-50">
            ({run.occupants.length}/{run.capacityMax})
          </span>
        )}
      </p>
      {primaryOccupant ? (
        <>
          <PetMiniCard
            occupant={primaryOccupant}
            draggable
            onDragStart={onDragStart}
            onClick={() => onOccupantsClick(run.occupants)}
          />
          {extraCount > 0 && (
            <button
              type="button"
              onClick={() => onOccupantsClick(run.occupants)}
              className="mt-1 min-h-11 rounded-sm bg-secondary/60 px-2 py-0.5 text-left text-[10px] font-medium text-muted-foreground hover:bg-secondary md:min-h-0"
            >
              +<span className="num">{extraCount}</span> more
            </button>
          )}
        </>
      ) : (
        <p className="mt-1 text-xs text-muted-foreground/50">Empty</p>
      )}
    </div>
  );
}

/** Compact single-row display for large facilities. */
interface RunCompactRowProps {
  run: TodaySnapshotRun;
  onOccupantsClick: (occupants: TodaySnapshotOccupant[]) => void;
}
function RunCompactRow({ run, onOccupantsClick }: RunCompactRowProps) {
  const occupied = run.occupants.length > 0;
  const names = run.occupants.map((o) => o.petName).join(', ');
  return (
    <li className="flex items-center gap-3 px-1 py-2 text-sm">
      <span className="num w-24 shrink-0 truncate text-xs font-medium text-muted-foreground">
        {run.name}
      </span>
      {occupied ? (
        <button
          type="button"
          onClick={() => onOccupantsClick(run.occupants)}
          className="min-w-0 truncate text-xs text-left hover:text-primary hover:underline"
        >
          {names}
          {run.capacityMax > 1 && (
            <span className="ml-1 text-muted-foreground">
              ({run.occupants.length}/{run.capacityMax})
            </span>
          )}
        </button>
      ) : (
        <span className="text-xs text-muted-foreground/50">Empty</span>
      )}
    </li>
  );
}

interface PetMiniCardProps {
  occupant: TodaySnapshotOccupant;
  draggable?: boolean;
  onDragStart?: (bookingId: string) => void;
  onClick?: () => void;
  onUnassign?: () => void;
  hideUnassign?: boolean;
}
function PetMiniCard({
  occupant,
  draggable = false,
  onDragStart,
  onClick,
  onUnassign,
  hideUnassign = false,
}: PetMiniCardProps) {
  const initials = occupant.petName.slice(0, 2).toUpperCase();
  return (
    <div
      draggable={draggable}
      onDragStart={(e) => {
        if (!draggable) return;
        e.dataTransfer.setData('text/booking-id', occupant.bookingId);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart?.(occupant.bookingId);
      }}
      className="group mt-1 flex min-h-11 items-center gap-2 rounded-md bg-card px-2 py-1.5 transition hover:shadow-sm md:min-h-0"
      data-testid={`pet-card-${occupant.bookingId}`}
    >
      <button
        type="button"
        onClick={onClick}
        className="flex min-h-11 min-w-0 flex-1 items-center gap-2 text-left md:min-h-0"
      >
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-medium text-primary"
          aria-hidden
        >
          {initials}
        </span>
        <span className="min-w-0">
          <span className="block truncate text-xs font-medium">{occupant.petName}</span>
          <span className="block truncate text-[10px] text-muted-foreground">
            out {occupant.checkout.slice(11, 16)}
          </span>
        </span>
      </button>
      <Link
        to={`/bookings/${occupant.bookingId}#messages`}
        className="flex min-h-11 min-w-8 items-center justify-center text-muted-foreground opacity-100 hover:text-primary md:min-h-0 md:min-w-0 md:opacity-0 md:group-hover:opacity-100"
        title="Message owner"
        aria-label={`Message ${occupant.customerName}`}
        data-testid={`message-owner-${occupant.bookingId}`}
        onClick={(e) => e.stopPropagation()}
      >
        <MessageSquare className="h-3.5 w-3.5" aria-hidden />
      </Link>
      {!hideUnassign && onUnassign && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onUnassign();
          }}
          className="text-muted-foreground opacity-100 hover:text-destructive md:opacity-0 md:group-hover:opacity-100"
          aria-label="Unassign"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
    </div>
  );
}

interface TimelineProps {
  events: TodaySnapshotEvent[];
  tz: string;
  onPick: (o: TodaySnapshotOccupant) => void;
}
function Timeline({ events, tz, onPick }: TimelineProps) {
  // Display 06:00–22:00 spread over a flex band; each event positioned by hour offset.
  const startHour = 6;
  const endHour = 22;
  const totalHours = endHour - startHour;

  const positioned = useMemo(() => {
    return events
      .map((e) => {
        try {
          const d = new Date(e.timeIso);
          const localHour = Number(
            new Intl.DateTimeFormat('en-US', {
              hour: '2-digit',
              hour12: false,
              timeZone: tz,
            }).format(d),
          );
          const minute = d.getMinutes() / 60;
          const hourFloat = localHour + minute;
          const pct = Math.max(0, Math.min(100, ((hourFloat - startHour) / totalHours) * 100));
          return { ...e, pct };
        } catch {
          return { ...e, pct: 0 };
        }
      })
      .filter((e) => e.pct >= 0 && e.pct <= 100);
  }, [events, tz]);

  return (
    <section data-testid="timeline">
      <h2 className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        Today timeline
      </h2>
      <div className="rounded-xl border border-border bg-surface px-4 py-4">
        {/* Below sm the 17-label ruler scrolls inside its own container
            (640px track) instead of widening the page. The horizontal padding
            leaves room for chips centred on the 06:00 / 22:00 ticks. */}
        <div className="-mx-2 overflow-x-auto overflow-y-hidden px-6 pt-1">
          <div className="relative h-16 min-w-[640px] sm:min-w-0" data-testid="timeline-track">
            {/* Hour ticks */}
            <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-border" />
            {Array.from({ length: totalHours + 1 }).map((_, i) => (
              <div
                key={i}
                className="absolute top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center"
                style={{ left: `${(i / totalHours) * 100}%` }}
              >
                <span className="h-1.5 w-px bg-border" />
                <span className="num mt-1 text-[9px] text-muted-foreground">
                  {String(startHour + i).padStart(2, '0')}
                </span>
              </div>
            ))}
            {/* Events */}
            {positioned.map((e, idx) => (
              <button
                type="button"
                key={`${e.bookingId}-${e.type}-${idx}`}
                onClick={() =>
                  onPick({
                    bookingId: e.bookingId,
                    petId: '',
                    petName: e.petName,
                    petSpecies: '',
                    petPhotoFileId: null,
                    customerId: '',
                    customerName: e.customerName,
                    customerPhone: null,
                    checkout: e.timeIso,
                    status: e.type,
                  })
                }
                className={`absolute -top-1 -translate-x-1/2 rounded-md border bg-background px-1.5 py-0.5 text-[10px] font-medium transition hover:z-10 hover:shadow ${
                  e.type === 'checkin'
                    ? 'border-emerald-500/50 text-emerald-700'
                    : e.type === 'checkout'
                      ? 'border-amber-500/50 text-amber-700'
                      : e.type === 'grooming'
                        ? 'border-violet-500/50 text-violet-700'
                        : 'border-blue-500/50 text-blue-700'
                }`}
                style={{ left: `${e.pct}%` }}
                title={`${e.petName} — ${e.customerName}`}
                data-testid={`timeline-event-${e.type}`}
              >
                {e.petName}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

interface SuggestionsRailProps {
  suggestions: TodaySnapshotSuggestion[];
  anomalies: Array<{ kind: string; severity: 'info' | 'warn' | 'critical'; message: string }>;
}
function SuggestionsRail({ suggestions, anomalies }: SuggestionsRailProps) {
  // Sort: critical first, then warn, then info.
  const severityOrder = { critical: 0, warn: 1, info: 2 };
  const items: TodaySnapshotSuggestion[] = [
    ...anomalies.map((a, i) => ({
      id: `anomaly-${i}`,
      severity: a.severity,
      title: a.message,
      body: 'Detected by AI Copilot.',
      actionLabel: 'Open Copilot',
      actionHref: '/settings/copilot',
    })),
    ...suggestions,
  ].sort(
    (a, b) =>
      (severityOrder[a.severity as keyof typeof severityOrder] ?? 2) -
      (severityOrder[b.severity as keyof typeof severityOrder] ?? 2),
  );

  return (
    <section data-testid="suggestions-rail">
      <h2 className="mb-2 text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
        Alerts &amp; suggestions
      </h2>
      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-secondary/30 px-4 py-6 text-center">
          <Circle className="mx-auto mb-2 h-4 w-4 text-success" aria-hidden />
          <p className="text-xs text-muted-foreground">Nothing flagged — quiet day ahead.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-surface divide-y divide-border">
          {items.map((s) => (
            <div
              key={s.id}
              className="flex items-start gap-3 px-4 py-3"
              data-testid={`suggestion-${s.id}`}
            >
              {/* Color-coded priority dot */}
              <span
                className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                  s.severity === 'critical'
                    ? 'bg-destructive'
                    : s.severity === 'warn'
                      ? 'bg-warning'
                      : 'bg-muted-foreground/40'
                }`}
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium leading-snug">{s.title}</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground leading-snug">{s.body}</p>
                <Link
                  to={s.actionHref}
                  className="mt-1.5 inline-block text-[11px] font-medium text-primary hover:underline"
                >
                  {s.actionLabel} →
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

interface OccupantDrawerProps {
  occupant: TodaySnapshotOccupant;
  tz: string;
  onClose: () => void;
  /** Migration 0100 — open the three-tap daily monitoring record. */
  onDailyCheck?: (occupant: TodaySnapshotOccupant) => void;
}
function OccupantDrawer({ occupant, tz, onClose, onDailyCheck }: OccupantDrawerProps) {
  const overstayNights = overstayNightsFor(occupant, tz);
  return (
    <div className="w-[min(96vw,32rem)] rounded-xl bg-background p-6">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold">{occupant.petName}</h3>
          <p className="mt-0.5 text-sm text-muted-foreground">{occupant.customerName}</p>
          {overstayNights > 0 && (
            <span
              className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-amber-200"
              data-testid="overstay-chip"
            >
              <Moon className="h-3 w-3" strokeWidth={1.75} aria-hidden />
              Overstay: <span className="num">{overstayNights}</span>{' '}
              {overstayNights === 1 ? 'night' : 'nights'}
            </span>
          )}
        </div>
        <button type="button" onClick={onClose} aria-label="Close">
          <X className="h-4 w-4" />
        </button>
      </div>
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt className="text-xs text-muted-foreground">Status</dt>
          <dd className="num">{occupant.status}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Checkout</dt>
          <dd className="num">
            {occupant.checkout ? formatTimeShort(occupant.checkout, tz) : '—'}
          </dd>
        </div>
        {occupant.customerPhone && (
          <div className="col-span-2">
            <dt className="text-xs text-muted-foreground">Phone</dt>
            <dd className="num">
              <a href={`tel:${occupant.customerPhone}`} className="text-primary hover:underline">
                {occupant.customerPhone}
              </a>
            </dd>
          </div>
        )}
      </dl>
      {occupant.bookingId && onDailyCheck && (
        <button
          type="button"
          onClick={() => onDailyCheck(occupant)}
          className="press mt-4 flex w-full items-center justify-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-muted"
        >
          <ClipboardCheck className="h-4 w-4" strokeWidth={1.75} aria-hidden />
          Daily check
        </button>
      )}
      <div className="mt-4 flex gap-2">
        {occupant.bookingId && (
          <Link
            to={`/bookings/${occupant.bookingId}`}
            className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-center text-sm font-medium hover:bg-muted"
            onClick={onClose}
          >
            Open booking
          </Link>
        )}
        {occupant.bookingId && (
          <Link
            to={`/bookings/${occupant.bookingId}#messages`}
            className="flex-1 rounded-md bg-primary px-3 py-2 text-center text-sm font-medium text-primary-foreground hover:opacity-90"
            onClick={onClose}
          >
            Message owner
          </Link>
        )}
      </div>
      {/* Pet report cards (migration 0093) — send straight from the floor plan. */}
      {occupant.bookingId && (
        <div className="mt-2 flex">
          <SendReportCardButton
            bookingId={occupant.bookingId}
            petName={occupant.petName}
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-muted"
          />
        </div>
      )}
      {/* Structured feeding log (migration 0101) — one tap per meal slot. */}
      {occupant.bookingId && <FeedingTicks bookingId={occupant.bookingId} />}
    </div>
  );
}

interface MultiOccupantDrawerProps {
  occupants: TodaySnapshotOccupant[];
  tz: string;
  onClose: () => void;
  onSelect: (o: TodaySnapshotOccupant) => void;
}
function MultiOccupantDrawer({ occupants, tz, onClose, onSelect }: MultiOccupantDrawerProps) {
  return (
    <div className="w-[min(96vw,32rem)] rounded-xl bg-background p-6">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold">
            <span className="num">{occupants.length}</span> pets in this run
          </h3>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Select a pet to view their booking detail.
          </p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close">
          <X className="h-4 w-4" />
        </button>
      </div>
      <ul className="divide-y divide-border">
        {occupants.map((o) => (
          <li key={o.bookingId}>
            <button
              type="button"
              onClick={() => onSelect(o)}
              className="flex w-full items-center gap-3 py-3 text-left hover:text-primary"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
                {o.petName.slice(0, 2).toUpperCase()}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{o.petName}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {o.customerName} — out{' '}
                  <span className="num">{formatTimeShort(o.checkout, tz)}</span>
                </span>
              </span>
              <span className="shrink-0 text-xs text-muted-foreground/50">View →</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface MetricDrawerProps {
  metric: 'occupancy' | 'revenue' | 'pets';
  snapshot: TodaySnapshot;
  onClose: () => void;
}
function MetricDrawer({ metric, snapshot, onClose }: MetricDrawerProps) {
  return (
    <div className="w-[min(96vw,32rem)] rounded-xl bg-background p-6" data-testid="metric-drawer">
      <div className="mb-4 flex items-start justify-between">
        <h3 className="text-lg font-semibold">
          {metric === 'occupancy'
            ? 'Occupancy'
            : metric === 'revenue'
              ? "Today's revenue"
              : 'Pets in care'}
        </h3>
        <button type="button" onClick={onClose} aria-label="Close">
          <X className="h-4 w-4" />
        </button>
      </div>
      {metric === 'occupancy' && (
        <dl className="space-y-3 text-sm">
          <Row
            k="Occupied"
            v={`${snapshot.metrics.occupancy.occupiedCount}/${snapshot.metrics.occupancy.totalCapacity}`}
          />
          <Row k="Percent" v={`${snapshot.metrics.occupancy.occupancyPct}%`} />
          <Row k="Same day last week" v={String(snapshot.metrics.occupancy.lastWeekCount)} />
        </dl>
      )}
      {metric === 'revenue' && (
        <dl className="space-y-3 text-sm">
          <Row
            k="Total in"
            v={formatPriceMinor(
              snapshot.metrics.revenue.totalMinor,
              snapshot.metrics.revenue.currency,
            )}
          />
          <Row k="Payments" v={String(snapshot.metrics.revenue.paymentCount)} />
        </dl>
      )}
      {metric === 'pets' && (
        <dl className="space-y-3 text-sm">
          <Row k="Boarding" v={String(snapshot.metrics.petsInCare.boarding)} />
          <Row k="Daycare" v={String(snapshot.metrics.petsInCare.daycare)} />
          <Row k="Grooming" v={String(snapshot.metrics.petsInCare.grooming)} />
          <Row k="Total" v={String(snapshot.metrics.petsInCare.total)} />
        </dl>
      )}
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center justify-between border-b border-border pb-2 last:border-0">
      <dt className="text-muted-foreground">{k}</dt>
      <dd className="num font-medium">{v}</dd>
    </div>
  );
}

interface BookingListItemProps {
  row: BookingSearchRow;
}

function BookingListItem({ row }: BookingListItemProps) {
  const noShowScore = Math.min(100, (row.noShowCount ?? 0) * 25);
  return (
    <li>
      <Link
        to={`/bookings/${row.bookingId}`}
        className="flex min-h-11 items-center justify-between gap-3 px-4 py-3 hover:bg-secondary/40 transition-colors"
      >
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{row.petName}</p>
          <div className="flex flex-wrap items-center gap-1.5">
            <p className="truncate text-xs text-muted-foreground">{row.customerName}</p>
            {noShowScore > 0 && (
              <NoShowBadge
                score={noShowScore}
                reasons={[
                  `${row.noShowCount ?? 0} prior no-show${(row.noShowCount ?? 0) > 1 ? 's' : ''}`,
                ]}
              />
            )}
          </div>
        </div>
        <div className="shrink-0 text-right">
          {row.runName && <p className="num text-xs text-muted-foreground">{row.runName}</p>}
        </div>
      </Link>
    </li>
  );
}

interface GroomingQueueItemProps {
  entry: PendingGroomingQueueEntry;
}

function GroomingQueueItem({ entry }: GroomingQueueItemProps) {
  return (
    <li
      className="flex items-center justify-between gap-3 px-4 py-3"
      data-testid={`grooming-queue-item-${entry.booking.id}`}
    >
      <div className="flex min-w-0 items-center gap-2">
        <Scissors className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">Grooming add-on (pending)</p>
          <p className="truncate text-xs text-muted-foreground">
            Booking {entry.booking.id.slice(-8)}
          </p>
        </div>
      </div>
      <Link
        to={`/bookings/${entry.booking.id}`}
        className="shrink-0 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
        data-testid={`assign-groomer-link-${entry.booking.id}`}
      >
        Assign groomer
      </Link>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Grooming schedule item — shows a single standalone grooming appointment.
// ---------------------------------------------------------------------------

interface GroomingScheduleItemProps {
  row: GroomingAppointmentRow;
  groomerName: string | null;
  snapshot: TodaySnapshot | null;
  timezone: string;
}

function GroomingScheduleItem({ row, groomerName, snapshot, timezone }: GroomingScheduleItemProps) {
  // Derive pet name and customer name from the snapshot events (no extra fetch).
  // Falls back to booking id suffix when not found (snapshot may not have loaded yet).
  const event = snapshot?.events.find(
    (e) => e.type === 'grooming' && e.bookingId === row.booking.id,
  );
  const petName = event?.petName ?? null;
  const customerName = event?.customerName ?? null;

  return (
    <li data-testid={`grooming-schedule-item-${row.booking.id}`}>
      <Link
        to={`/bookings/${row.booking.id}`}
        className="flex min-h-11 items-center justify-between gap-3 px-4 py-3 hover:bg-secondary/40 transition-colors"
      >
        <div className="flex min-w-0 items-center gap-2">
          <Scissors className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">
              {petName ?? `Booking ${row.booking.id.slice(-8)}`}
            </p>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
              {customerName && (
                <span className="text-xs text-muted-foreground">{customerName}</span>
              )}
              <span className="flex items-center gap-1 num text-xs text-muted-foreground">
                <Clock className="h-3 w-3 shrink-0" aria-hidden />
                {formatTimeShort(row.appointment.startAt, timezone)}
              </span>
              {groomerName && (
                <span className="text-xs text-muted-foreground">· {groomerName}</span>
              )}
            </div>
          </div>
        </div>
        <StatusPill status={row.booking.status} />
      </Link>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Task #27 — Print today's run cards header button. Fetches the batched
// HTML from /api/run-cards/batch and opens it in a new tab with auto-print.
// ---------------------------------------------------------------------------
function PrintTodaysRunCards({ facilityId, date }: { facilityId: string; date: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handle() {
    setBusy(true);
    setError(null);
    try {
      const win = await openBatchRunCardsForPrint(facilityId, date, 'full');
      if (!win) setError('Pop-up blocked.');
    } catch (e) {
      setError((e as Error).message ?? 'Print failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center gap-2">
      {error && <span className="text-xs text-destructive">{error}</span>}
      <button
        type="button"
        onClick={handle}
        disabled={busy}
        className="inline-flex min-h-11 items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium hover:bg-secondary disabled:opacity-50 md:min-h-0"
        data-testid="print-todays-run-cards"
      >
        <Printer className="size-3.5" strokeWidth={1.75} aria-hidden />
        {busy ? 'Preparing…' : "Print today's run cards"}
      </button>
    </div>
  );
}
